import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send, Loader2, MessageSquare, BookOpen, RotateCcw, Copy, Check } from "lucide-react";
import styles from "./SOPAskModal.module.css";
import sopService from "../../../services/sop.service";
import { SOP } from "../../../types/sop";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface SOPAskModalProps {
  sop: SOP;
  onClose: () => void;
}

const SOPAskModal: React.FC<SOPAskModalProps> = ({ sop, onClose }) => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
  };

  const handleSubmit = async () => {
    const q = question.trim();
    if (!q || loading) return;

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      content: q,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setQuestion("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setLoading(true);

    try {
      const res = await sopService.askSOPQuestion(sop.id, q);
      const assistantMsg: Message = {
        id: `a-${Date.now()}`,
        role: "assistant",
        content: res.answer,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const isNotFound =
        err?.message?.toLowerCase().includes("not found") ||
        err?.message?.toLowerCase().includes("no information") ||
        err?.message?.toLowerCase().includes("don't know") ||
        err?.message?.toLowerCase().includes("cannot find");

      const errorMsg: Message = {
        id: `e-${Date.now()}`,
        role: "assistant",
        content: isNotFound
          ? "I wasn't able to find a specific answer to that in this SOP. Try rephrasing your question, or ask about a different section like billing rules, CPT codes, or provider details."
          : "Hmm, something went wrong on my end. Please try asking again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCopy = (msg: Message) => {
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopiedId(msg.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleClear = () => {
    setMessages([]);
    setQuestion("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    textareaRef.current?.focus();
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Ask questions about ${sop.title}`}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <MessageSquare size={16} />
            </div>
            <div className={styles.headerMeta}>
              <h2 className={styles.headerTitle}>Ask SOP</h2>
              <div className={styles.headerSub}>
                <BookOpen size={11} />
                <span>{sop.title}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            {messages.length > 0 && (
              <button
                className={styles.clearBtn}
                onClick={handleClear}
                title="Clear conversation"
              >
                <RotateCcw size={14} />
                <span>Clear</span>
              </button>
            )}
            <button className={styles.closeBtn} onClick={onClose} title="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <MessageSquare size={28} />
              </div>
              <p className={styles.emptyTitle}>Ask anything about this SOP</p>
              <p className={styles.emptySubtitle}>
                Get instant answers from the document's content — billing rules,
                coding guidelines, provider details, and more.
              </p>
              <div className={styles.suggestedQuestions}>
                {[
                  "What are the billing guidelines?",
                  "What CPT codes are covered?",
                  "Who is the billing provider?",
                ].map((q) => (
                  <button
                    key={q}
                    className={styles.suggestionChip}
                    onClick={() => {
                      setQuestion(q);
                      textareaRef.current?.focus();
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div
              className={`${styles.messagesWrapper}`}
            >
              <div className={styles.messages}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${styles.messageBubble} ${msg.role === "user"
                        ? styles.userBubble
                        : styles.assistantBubble
                      }`}
                  >
                    <div className={styles.bubbleInner}>
                      <p className={styles.bubbleText}>{msg.content}</p>
                      <div className={styles.bubbleMeta}>
                        <span className={styles.bubbleTime}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {msg.role === "assistant" && (
                          <button
                            className={styles.copyBtn}
                            onClick={() => handleCopy(msg)}
                            title="Copy answer"
                          >
                            {copiedId === msg.id ? (
                              <Check size={11} />
                            ) : (
                              <Copy size={11} />
                            )}
                            {copiedId === msg.id ? "Copied" : "Copy"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className={`${styles.messageBubble} ${styles.assistantBubble}`}>
                    <div className={styles.bubbleInner}>
                      <div className={styles.typingIndicator}>
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>

        {/* Footer / Input */}
        <div className={styles.footer}>
          <div className={styles.inputRow}>
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              placeholder="Ask a question about this SOP… (Enter to send)"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                autoResize();
              }}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              className={styles.sendBtn}
              onClick={handleSubmit}
              disabled={!question.trim() || loading}
              title="Send question"
            >
              {loading ? (
                <Loader2 size={16} className={styles.spin} />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
          <p className={styles.footerHint}>
            Shift + Enter for new line &nbsp;·&nbsp; Enter to send
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SOPAskModal;